import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  CheckboxInput,
  Dialog,
  Form,
  FormButton,
  ImageUploader,
  Input,
  string,
  useNotifications
} from '@ijoin/components';
import {
  UploadAssetBodyContentType,
  useGetAdminAdvisoryOrganization,
  useGetAdminAdvisoryOrganizationRecordKeepingOrganizations,
  usePatchAdminAdvisoryOrganization,
  useUploadAsset
} from '@ijoin/hooks';
import { Close } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { assetUrl } from '../helpers';

type Option = { label: string; value: string };

type EditFormProps = {
  addressOne: string;
  addressTwo: string;
  areNewSyncsEnabled: boolean;
  billingRecordKeepingOrg: null | Option;
  city: string;
  hasInvestorView: boolean;
  hasIraAccountOpeningExperience: boolean;
  hasManifest: boolean;
  hasV2Enrollment: boolean;
  logoUri: string;
  name: string;
  s3Namespace: string;
  state: string;
  website: string;
  zip: string;
};

const resolver = zodResolver(
  z.object({
    addressOne: string(),
    addressTwo: string(),
    areNewSyncsEnabled: z.boolean(),
    billingRecordKeepingOrg: z
      .object({ label: z.string(), value: z.string() })
      .nullable(),
    city: string(),
    hasInvestorView: z.boolean(),
    hasIraAccountOpeningExperience: z.boolean(),
    hasManifest: z.boolean(),
    hasV2Enrollment: z.boolean(),
    logoUri: string(),
    name: string({ maxLength: 100, required: true }),
    s3Namespace: string(),
    state: string(),
    website: string(),
    zip: string()
  })
);

export const EditOrganizationDialog: FC<{
  advisoryOrganizationId: string;
  isOpen: boolean;
  onClose: () => void;
}> = props => {
  const fullScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const org = useGetAdminAdvisoryOrganization(props.advisoryOrganizationId);
  const linkedRkos = useGetAdminAdvisoryOrganizationRecordKeepingOrganizations(
    props.advisoryOrganizationId
  );

  const linkedRkoOptions = (linkedRkos.data ?? []).map(rko => ({
    label: rko.name,
    value: rko.recordKeepingOrganizationId
  }));

  const billingRkoId = org.data?.billingRecordKeepingOrgId;

  /** an org set up before billing was scoped to linked record keepers can point at an unlinked one, so keep it selectable rather than silently dropping it */
  const isBillingRkoUnlinked =
    !!billingRkoId &&
    linkedRkos.isSuccess &&
    !linkedRkoOptions.some(option => option.value === billingRkoId);

  const rkoOptions = isBillingRkoUnlinked
    ? [
        ...linkedRkoOptions,
        {
          label: `${org.data?.RecordKeepingOrganizations?.name ?? billingRkoId} (not linked)`,
          value: billingRkoId
        }
      ]
    : linkedRkoOptions;

  const defaultValues: EditFormProps = {
    addressOne: org.data?.addressOne ?? '',
    addressTwo: org.data?.addressTwo ?? '',
    areNewSyncsEnabled: org.data?.areNewSyncsEnabled ?? false,
    billingRecordKeepingOrg: billingRkoId
      ? (rkoOptions.find(option => option.value === billingRkoId) ?? null)
      : null,
    city: org.data?.city ?? '',
    hasInvestorView: org.data?.hasInvestorView ?? false,
    hasIraAccountOpeningExperience:
      org.data?.hasIraAccountOpeningExperience ?? false,
    hasManifest: org.data?.hasManifest ?? false,
    hasV2Enrollment: org.data?.hasV2Enrollment ?? false,
    logoUri: org.data?.logoUri ?? '',
    name: org.data?.name ?? '',
    s3Namespace: org.data?.s3Namespace ?? '',
    state: org.data?.state ?? '',
    website: org.data?.website ?? '',
    zip: org.data?.zip ?? ''
  };

  const methods = useForm<EditFormProps>({
    defaultValues,
    mode: 'all',
    resolver,
    values: defaultValues
  });

  const updateOrg = usePatchAdminAdvisoryOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to update advisory organization'),
      onSuccess: () => {
        notifications.success('Advisory organization updated successfully');
        queryClient.invalidateQueries({
          queryKey: [
            `/api/v3/admin/advisory-organizations/${props.advisoryOrganizationId}`
          ]
        });
        props.onClose();
      }
    }
  });

  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload logo')
    }
  });

  const logoUri = methods.watch('logoUri');

  const onSubmit = (data: EditFormProps) => {
    const { billingRecordKeepingOrg, ...rest } = data;
    updateOrg.mutate({
      advisoryOrganizationId: props.advisoryOrganizationId,
      data: {
        ...rest,
        billingRecordKeepingOrgId: billingRecordKeepingOrg?.value ?? null
      }
    });
  };

  const onCancel = () => {
    methods.reset(defaultValues, { keepDirtyValues: false });
    props.onClose();
  };

  const isSaveDisabled =
    !methods.formState.isDirty || uploadAsset.isPending || updateOrg.isPending;

  return (
    <Dialog
      data-testid='editAdvisoryOrganizationDialog'
      fullScreen={fullScreen}
      fullWidth
      isOpen={props.isOpen}
      maxWidth='md'
      onClose={onCancel}>
      <DialogTitle
        component='div'
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          pl: 3,
          pr: 1,
          py: 2
        }}>
        <Typography variant='h3'>Edit Advisory Organization</Typography>
        <IconButton
          aria-label='Close'
          data-testid='closeEditAdvisoryOrganization'
          onClick={onCancel}>
          <Close />
        </IconButton>
      </DialogTitle>
      {org.isLoading ? (
        <DialogContent dividers>
          <Stack alignItems='center' justifyContent='center' sx={{ p: 6 }}>
            <CircularProgress />
          </Stack>
        </DialogContent>
      ) : (
        <Form
          data-testid='editAdvisoryOrganizationForm'
          methods={methods}
          noValidate
          onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant='h4'>Organization Info</Typography>
                <Divider />

                <Stack
                  alignItems='center'
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={3}>
                  <ImageUploader
                    alt='Logo preview'
                    data-testid='editOrgLogoUri'
                    emptyPlaceholder='No logo uploaded'
                    invalidTypeMessage='Logo must be a PNG, JPEG, or WebP image.'
                    isUploading={uploadAsset.isPending}
                    label='Upload logo'
                    onChange={key =>
                      methods.setValue('logoUri', key, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    onUpload={({ content, contentType }) =>
                      uploadAsset.mutateAsync({
                        data: {
                          content,
                          contentType: contentType as UploadAssetBodyContentType
                        },
                        kind: 'logos'
                      })
                    }
                    previewSrc={logoUri ? assetUrl(logoUri) : undefined}
                    previewVariant='image'
                    registerCtx={methods.register('logoUri')}
                    value={logoUri}
                  />
                  <Stack flex={1} spacing={2} width='100%'>
                    <Input
                      data-testid='editOrgName'
                      format='string'
                      fullWidth
                      label='Name'
                      name='name'
                      required
                      size='small'
                    />
                    <Input
                      data-testid='editOrgWebsite'
                      format='string'
                      fullWidth
                      label='Website'
                      name='website'
                      size='small'
                    />
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Address</Typography>
                <Divider />

                <Input
                  data-testid='editOrgAddressOne'
                  format='string'
                  fullWidth
                  label='Address Line 1'
                  name='addressOne'
                  size='small'
                />
                <Input
                  data-testid='editOrgAddressTwo'
                  format='string'
                  fullWidth
                  label='Address Line 2'
                  name='addressTwo'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='editOrgCity'
                    format='string'
                    fullWidth
                    label='City'
                    name='city'
                    size='small'
                  />
                  <Input
                    data-testid='editOrgState'
                    format='string'
                    fullWidth
                    label='State'
                    name='state'
                    size='small'
                  />
                  <Input
                    data-testid='editOrgZip'
                    format='string'
                    fullWidth
                    label='Zip'
                    name='zip'
                    size='small'
                  />
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Settings</Typography>
                <Divider />

                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <CheckboxInput
                    data-testid='editOrgHasV2Enrollment'
                    hideError
                    name='hasV2Enrollment'
                    option='Has V2 Enrollment'
                  />
                  <CheckboxInput
                    data-testid='editOrgHasManifest'
                    hideError
                    name='hasManifest'
                    option='Has Manifest'
                  />
                  <CheckboxInput
                    data-testid='editOrgHasInvestorView'
                    hideError
                    name='hasInvestorView'
                    option='Has InvestorView'
                  />
                  <CheckboxInput
                    data-testid='editOrgHasIraAccountOpeningExperience'
                    hideError
                    name='hasIraAccountOpeningExperience'
                    option='Has IRA Account Opening Experience'
                  />
                </Stack>
                <Autocomplete<Option>
                  data-testid='editOrgBillingRecordKeeper'
                  fullWidth
                  helperText='Only linked record keepers can be billed. ACT Analytics licensing is inherited from this record keeper.'
                  label='Billing Record Keeper'
                  name='billingRecordKeepingOrg'
                  options={rkoOptions}
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Sync Pipeline</Typography>
                <Divider />

                <Input
                  data-testid='editOrgS3Namespace'
                  format='string'
                  fullWidth
                  label='S3 Namespace'
                  name='s3Namespace'
                  readOnly
                  size='small'
                />
                <CheckboxInput
                  data-testid='editOrgAreNewSyncsEnabled'
                  hideError
                  name='areNewSyncsEnabled'
                  option='New Syncs Enabled'
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelEditOrg'
              onClick={onCancel}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveEditOrg'
              disabled={isSaveDisabled}
              triggerValidation
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

EditOrganizationDialog.displayName = 'EditOrganizationDialog';
